import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AirplaneIcon from './AirplaneIcon';
import { trackEmailCapture } from '../services/trackingService';

const SiteFooter: React.FC = () => {
  const [dispatchEmail, setDispatchEmail] = useState('');
  const [dispatchStatus, setDispatchStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchEmail.trim()) return;
    setDispatchStatus('sending');
    try {
      const { db } = await import('../services/firebase');
      const { collection, addDoc, query, where, getDocs, limit, serverTimestamp } = await import('firebase/firestore');
      if (!db) throw new Error('Firestore unavailable');
      const subscribersRef = collection(db, 'newsletter_subscribers');
      const existing = await getDocs(query(subscribersRef, where('email', '==', dispatchEmail.trim().toLowerCase()), limit(1)));
      if (!existing.empty) { setDispatchStatus('success'); setDispatchEmail(''); return; }
      await addDoc(subscribersRef, {
        email: dispatchEmail.trim().toLowerCase(),
        source: 'footer',
        subscribedAt: serverTimestamp(),
      });
      trackEmailCapture('footer');
      setDispatchStatus('success');
      setDispatchEmail('');
    } catch {
      setDispatchStatus('error');
    }
  };

  const linkClasses = "font-sans text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-gold transition-colors duration-300";

  return (
    <footer className="bg-[#050505] border-t border-white/5 py-20 px-4 sm:px-6 md:px-12">
      <div className="max-w-7xl mx-auto">

        {/* Newsletter Section */}
        <div className="border-b border-white/5 pb-16 mb-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div className="max-w-sm">
              <h4 className="font-display text-xl text-white tracking-tight mb-2">The Dispatch</h4>
              <p className="font-serif text-sm text-white/60 italic leading-relaxed">
                Curated destination intelligence and insider travel insights.
              </p>
            </div>
            {dispatchStatus === 'success' ? (
              <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-gold">
                Welcome to The Dispatch.
              </p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto liquid-glass-pill p-1.5 sm:p-2">
                <input
                  type="email"
                  name="email"
                  value={dispatchEmail}
                  onChange={(e) => setDispatchEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  aria-label="Email address for The Dispatch newsletter"
                  className="flex-1 md:w-56 px-5 py-3 bg-transparent text-white font-sans text-sm placeholder:text-white/50 focus:outline-none transition-colors duration-300"
                />
                <button
                  type="submit"
                  disabled={dispatchStatus === 'sending'}
                  className="px-6 py-3 bg-gold text-black font-sans text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-h-[44px] rounded-full press-haptic"
                >
                  {dispatchStatus === 'sending' ? '...' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>
          {dispatchStatus === 'error' && (
            <p className="mt-3 font-sans text-[11px] text-red-400/80 tracking-wide text-right">
              Something went wrong. Please try again.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-3 group mb-6">
              <AirplaneIcon className="w-5 h-5 text-white/60 group-hover:text-gold transition-colors duration-500" />
              <div className="flex flex-col">
                <span className="font-display text-lg tracking-[0.3em] uppercase leading-none text-white/80">
                  Aura
                </span>
                <span className="font-serif text-[10px] italic tracking-[0.4em] text-gold/70 mt-0.5 uppercase">
                  Traveler
                </span>
              </div>
            </Link>
            <p className="font-serif text-sm text-white/60 leading-relaxed max-w-[200px]">
              Intelligent luxury travel design for agencies, hotels, and discerning travelers.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-6">
              Product
            </h4>
            <ul className="space-y-4">
              <li><Link to="/about" className={linkClasses}>About</Link></li>
              <li><Link to="/philosophy" className={linkClasses}>Philosophy</Link></li>
              <li><Link to="/pricing" className={linkClasses}>Pricing</Link></li>
              <li><Link to="/journal" className={linkClasses}>Journal</Link></li>
              <li><Link to="/field-notes" className={linkClasses}>Field Notes</Link></li>
              <li><Link to="/faq" className={linkClasses}>FAQ</Link></li>
              <li><Link to="/day-planner" className={linkClasses}>Day Planner</Link></li>
              <li><Link to="/explore" className={linkClasses}>Explore</Link></li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-6">
              Solutions
            </h4>
            <ul className="space-y-4">
              <li><Link to="/for-agencies" className={linkClasses}>For Agencies</Link></li>
              <li><Link to="/for-hotels" className={linkClasses}>For Hotels</Link></li>
              <li><Link to="/contact" className={linkClasses}>Contact</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-6">
              Legal
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-6">
              Follow
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="https://instagram.com/auratraveler_com" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  Instagram
                </a>
              </li>
              <li>
                <a href="https://x.com/AuraTraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  X / Twitter
                </a>
              </li>
              <li>
                <a href="https://linkedin.com/company/auratraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  LinkedIn
                </a>
              </li>
              <li>
                <a href="https://facebook.com/auratraveler" target="_blank" rel="noopener noreferrer" className={linkClasses}>
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/60">
            &copy; 2026 Aura Traveler. Aura Engine v3.1.
          </p>
          <p className="font-serif text-[10px] italic text-white/50">
            Crafted for the discerning traveler.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
